'use client';

import Image from 'next/image';
import { FacebookShareButton, WhatsappShareButton } from 'react-share';
import { Button as ForgeButton, ButtonProps, Flex, VisuallyHidden } from '@mezzoforte/forge';
import type { ComponentWithAs } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import useSendAnalytics from '@/hooks/useSendAnalytics';
import { gtmService } from '@/util/gtm-service';
import FacebookShareIcon from 'public/social/fb-share-icon.svg';
import WhatsAppShareIcon from 'public/social/whatsapp-share-icon.svg';

const socialComponents = {
  whatsapp: [WhatsAppShareIcon, WhatsappShareButton],
  facebook: [FacebookShareIcon, FacebookShareButton],
} as const;

interface ShareButtonProps extends ButtonProps {
  readonly entryId: number;
  readonly service: keyof typeof socialComponents;
}

function ShareButton({ entryId, service, ...rest }: ShareButtonProps) {
  const sendAnalytics = useSendAnalytics('share', {
    sharingType: service,
    sharingElement: `${service}_share_button`,
    entryId,
  });

  // Render URL as empty string on server, replace on client with page's url
  const [url, setUrl] = useState('');
  useEffect(() => setUrl(window.location.href), []);

  const [Icon, ReactShareButton] = socialComponents[service];

  const Button = ForgeButton as ComponentWithAs<typeof ForgeButton, ButtonProps>;

  return (
    <Button
      as={ReactShareButton}
      variant="secondary-lists"
      url={url}
      onShareWindowClose={() => {
        sendAnalytics();
        gtmService.recommended.share(service === 'whatsapp' ? 'WhatsApp' : 'Facebook', 'auction', String(entryId));
      }}
      resetButtonStyle={false}
      data-test={`share-button-${service}`}
      {...rest}
      aria-label={`Jaa palvelussa ${service}`}
    >
      <Flex direction="row-reverse">
        Jaa <VisuallyHidden>palvelussa</VisuallyHidden>
        <Flex pr={1}>
          <Image
            src={Icon} // eslint-disable-line @typescript-eslint/no-unsafe-assignment
            alt={service}
            height={16}
          />
        </Flex>
      </Flex>
    </Button>
  );
}

export default ShareButton;
